import React, { useMemo, useState } from 'react';
import { uploadCrmLeadCsv, uploadCrmPmsgCsv } from '../config/api';
import { saveBlob } from '../utils/nativeFiles';

const SUMMARY_FIELDS = [
  ['Rows processed', 'processedRows'],
  ['Leads created', 'createdLeads'],
  ['Existing leads matched', 'existingLeads'],
  ['Contacts created', 'contactsCreated'],
  ['GST links created', 'gstLinksCreated'],
  ['Hub links created', 'hubLinksCreated'],
  ['Existing Hub links', 'existingHubLinks'],
];

const csvCell = (value) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;

const CrmLeadCsvImportDialog = ({ hubOptions = [], onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [importType, setImportType] = useState('pmsg');
  const [hubId, setHubId] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState([]);
  const [result, setResult] = useState(null);

  const selectableHubs = useMemo(
    () => hubOptions.filter((hub) => Number.isSafeInteger(Number(hub.value)) && Number(hub.value) > 0),
    [hubOptions]
  );

  const changeImportType = (nextType) => {
    if (nextType === importType || uploading) return;
    setImportType(nextType);
    setFile(null);
    setHubId('');
    setResult(null);
    setError('');
    setErrorDetails([]);
  };

  const selectFile = (nextFile) => {
    if (nextFile && !String(nextFile.name || '').toLowerCase().endsWith('.csv')) {
      setFile(null);
      setError('Please select a CSV file.');
      setErrorDetails([]);
      setResult(null);
      return;
    }
    setFile(nextFile || null);
    setError('');
    setErrorDetails([]);
    setResult(null);
  };

  const fileSize = file
    ? file.size < 1024 * 1024
      ? `${Math.max(1, Math.round(file.size / 1024))} KB`
      : `${(file.size / (1024 * 1024)).toFixed(1)} MB`
    : '';

  const downloadSample = () => {
    const rows = importType === 'pmsg'
      ? [
          [
            'snapshot_date', 'state', 'district', 'vendor_name', 'contact_person',
            'mobile', 'email', 'address', 'website', 'vendor_id', 'state_installs',
            'state_capacity_kw', 'national_installs', 'national_capacity_kw',
            'district_installs', 'district_capacity_kw', 'data_as_of',
          ],
          [
            new Date().toISOString().slice(0, 10), 'UTTAR PRADESH', 'Lucknow',
            'Sample PMSG Vendor', 'Sample Contact', '9876543210',
            'contact@example.com', 'Full vendor address', 'https://example.com',
            'PMSG_VENDOR_ID_HERE', '100', '500', '150', '750', '60', '300',
            new Date().toISOString().slice(0, 10),
          ],
        ]
      : [
          ['company_name', 'phone_number', 'gst'],
          ['Sample Normal Lead', '9876543210', '09ABCDE1234F1Z5'],
          ['Sample Phone-only Lead', '9876543211', ''],
        ];
    const csv = `\uFEFF${rows.map((row) => row.map(csvCell).join(',')).join('\r\n')}\r\n`;
    saveBlob(
      new Blob([csv], { type: 'text/csv;charset=utf-8' }),
      importType === 'pmsg'
        ? 'crm-pmsg-complete-import-sample.csv'
        : 'crm-lead-import-sample.csv'
    );
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!file) {
      setError('Select a CSV file.');
      return;
    }
    setUploading(true);
    setError('');
    setErrorDetails([]);
    setResult(null);
    try {
      let imported;
      if (importType === 'pmsg') {
        const response = await uploadCrmPmsgCsv(file, hubId || null);
        const result = response?.data || response;
        imported = {
          type: 'pmsg',
          snapshotImport: result.snapshotImport,
          leadSync: result.leadSync,
        };
      } else {
        const response = await uploadCrmLeadCsv(file, hubId || null);
        imported = response?.data || response;
      }
      setResult(imported);
      if (onImported) await onImported(imported);
    } catch (err) {
      setError(err.message || 'Could not import Lead CSV.');
      setErrorDetails(Array.isArray(err.details) ? err.details.slice(0, 10) : []);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={uploading ? undefined : onClose}>
      <div className="modal-content crm-lead-import-modal" onClick={(event) => event.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="modal-header crm-lead-import-header">
            <div className="crm-lead-import-title-wrap">
              <div className="crm-lead-import-title-icon">CSV</div>
              <div>
              <h3>Import Leads from CSV</h3>
                <p className="crm-modal-subtitle">Use a template, review the options, and import with confidence.</p>
              </div>
            </div>
            <button type="button" className="modal-close" onClick={onClose} disabled={uploading}>×</button>
          </div>
          <div className="modal-body crm-lead-import-body">
            <section className="crm-lead-import-section">
              <div className="crm-lead-import-step-heading">
                <span className="crm-lead-import-step-number">1</span>
                <div>
                  <strong>Choose the data type</strong>
                  <span>This controls the CSV format and import process.</span>
                </div>
              </div>

              <div className="crm-lead-import-type-grid" role="radiogroup" aria-label="CSV data type">
                <button
                  type="button"
                  role="radio"
                  aria-checked={importType === 'pmsg'}
                  className={`crm-lead-import-type-card pmsg ${importType === 'pmsg' ? 'selected' : ''}`}
                  onClick={() => changeImportType('pmsg')}
                  disabled={uploading}
                >
                  <span className="crm-lead-import-type-mark">P</span>
                  <span>
                    <strong>PMSG vendor snapshot</strong>
                    <small>Store vendor and district data, then sync Leads.</small>
                  </span>
                  <span className="crm-lead-import-radio" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={importType === 'normal'}
                  className={`crm-lead-import-type-card normal ${importType === 'normal' ? 'selected' : ''}`}
                  onClick={() => changeImportType('normal')}
                  disabled={uploading}
                >
                  <span className="crm-lead-import-type-mark">L</span>
                  <span>
                    <strong>Normal CRM Leads</strong>
                    <small>Import Leads and match existing phone or GST.</small>
                  </span>
                  <span className="crm-lead-import-radio" aria-hidden="true" />
                </button>
              </div>

              <div className="crm-lead-import-format-row">
                <p>
                  {importType === 'pmsg' ? (
                    <><strong>Required values:</strong> snapshot date, state, vendor name, contact person, 10-digit mobile and vendor ID.</>
                  ) : (
                    <>Use <strong>company_name</strong> and <strong>phone_number</strong>. GST is optional.</>
                  )}
                </p>
                <button type="button" className="crm-lead-import-template-btn" onClick={downloadSample} disabled={uploading}>
                  <span aria-hidden="true">↓</span> Download {importType === 'pmsg' ? 'PMSG' : 'Lead'} template
                </button>
              </div>
            </section>

            <section className="crm-lead-import-section">
              <div className="crm-lead-import-step-heading">
                <span className="crm-lead-import-step-number">2</span>
                <div>
                  <strong>Choose a Hub <em>optional</em></strong>
                  <span>Apply one Hub to every imported or matched Lead.</span>
                </div>
              </div>
              <select
                id="crm-lead-import-hub"
                className="form-control crm-lead-import-hub-select"
                value={hubId}
                onChange={(event) => setHubId(event.target.value)}
                disabled={uploading}
                aria-label="Assign all rows to Hub"
              >
                <option value="">Do not assign a Hub</option>
                {selectableHubs.map((hub) => <option key={hub.value} value={hub.value}>{hub.label}</option>)}
              </select>
              <div className="crm-form-help">Existing Hub links will remain unchanged.</div>
            </section>

            <section className="crm-lead-import-section">
              <div className="crm-lead-import-step-heading">
                <span className="crm-lead-import-step-number">3</span>
                <div>
                  <strong>Upload your CSV</strong>
                  <span>{importType === 'pmsg' ? 'The snapshot will be stored before Lead sync runs.' : 'Existing Leads will be matched before new Leads are created.'}</span>
                </div>
              </div>
              <input
                id="crm-lead-csv"
                className="crm-lead-import-file-input"
                type="file"
                accept=".csv,text/csv"
                key={importType}
                onChange={(event) => selectFile(event.target.files?.[0] || null)}
                disabled={uploading}
              />
              <label
                htmlFor="crm-lead-csv"
                className={`crm-lead-import-dropzone ${dragActive ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
                onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
                onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
                onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragActive(false);
                  selectFile(event.dataTransfer.files?.[0] || null);
                }}
              >
                <span className="crm-lead-import-upload-icon" aria-hidden="true">↑</span>
                {file ? (
                  <span className="crm-lead-import-file-selected">
                    <strong>{file.name}</strong>
                    <small>{fileSize} · Ready to import</small>
                    <em>Click to replace</em>
                  </span>
                ) : (
                  <span>
                    <strong>Drop your CSV here</strong>
                    <small>or click to browse · CSV files only</small>
                  </span>
                )}
              </label>
            </section>

            {error && <div className="crm-form-error">{error}</div>}
            {errorDetails.length > 0 && (
              <div className="crm-variable-import-rows">
                {errorDetails.map((detail, index) => (
                  <div key={`${detail.row || 'error'}-${index}`}>
                    {detail.row ? `Row ${detail.row}: ` : ''}{detail.message || String(detail)}
                  </div>
                ))}
              </div>
            )}
            {result && result.type !== 'pmsg' && (
              <div className="crm-variable-import-summary">
                <strong>Import completed</strong>
                <div className="crm-lead-import-summary-grid">
                  {SUMMARY_FIELDS.map(([label, field]) => (
                    <div key={field}><span>{label}</span><strong>{Number(result[field] || 0).toLocaleString('en-IN')}</strong></div>
                  ))}
                </div>
              </div>
            )}
            {result?.type === 'pmsg' && (
              <div className="crm-variable-import-summary">
                <strong>PMSG import and Lead sync completed</strong>
                <div className="crm-lead-import-summary-grid">
                  <div><span>Snapshot date</span><strong>{result.snapshotImport?.snapshot?.snapshotDate || '—'}</strong></div>
                  <div><span>Vendor rows stored</span><strong>{Number(result.snapshotImport?.vendors?.inserted || 0) + Number(result.snapshotImport?.vendors?.updated || 0)}</strong></div>
                  <div><span>District rows stored</span><strong>{Number(result.snapshotImport?.districts?.inserted || 0) + Number(result.snapshotImport?.districts?.updated || 0)}</strong></div>
                  <div><span>Leads created</span><strong>{Number(result.leadSync?.createdLeads || 0)}</strong></div>
                  <div><span>Already linked</span><strong>{Number(result.leadSync?.alreadyLinked || 0)}</strong></div>
                  <div><span>Hub links assigned</span><strong>{Number(result.leadSync?.hubAssignments?.assigned || 0)}</strong></div>
                  {result.leadSync?.explicitHubAssignment && (
                    <div><span>Selected Hub assigned</span><strong>{Number(result.leadSync.explicitHubAssignment.assigned || 0)}</strong></div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={uploading}>{result ? 'Close' : 'Cancel'}</button>
            {!result && (
              <button type="submit" className="btn btn-primary" disabled={uploading || !file}>
                {uploading ? 'Importing…' : importType === 'pmsg' ? 'Import and sync PMSG' : 'Import Leads'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmLeadCsvImportDialog;
